import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Box, TextField, Typography } from '@mui/material';
import { styled } from '@mui/system';
import Divider from '@mui/material/Divider';
import RTL from '../utils/RTL';
import StyledButton from '../components/CustomButton';
import api from '../config/axiosConfig';
import logo from '../assets/logo.png';

const PageBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  backgroundColor: theme.palette.background.neutral,
}));

const InnerBox = styled(Box)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.background.default,
  minHeight: '50vh',
  minWidth: '25%',
}));

const StyledForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  '& > :not(style)': {
    marginBottom: theme.spacing(2),
    width: '100%',
  },
}));

const StyledLogo = styled('img')(({ theme }) => ({
  width: '11rem',
  maxWidth: '60%',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
}));

const AccentLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.primary.main,
  fontWeight: 500,
  '&:hover': {
    textDecoration: 'underline',
  },
}));

// The email link carries base64url(JWT); the confirm endpoint expects the raw JWT.
const decodeResetToken = (encoded) => {
  const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
  return atob(b64);
};

function RequestForm() {
  const [email, setEmail] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [sent, setSent] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!email) {
      setErr('ایمیل الزامی است.');
      return;
    }
    try {
      setSubmitting(true);
      await api.post('/accounts/api/v1/password-reset/', { email });
      setSent(true);
    } catch (e) {
      if (e?.response?.status === 404) {
        setErr('کاربری با این ایمیل یافت نشد.');
      } else {
        setErr(e?.response?.data?.detail || 'ارسال ایمیل ناموفق بود. لطفاً بعداً دوباره تلاش کنید.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <Typography sx={{ textAlign: 'center', my: 4 }}>
        لینک بازیابی رمز عبور به ایمیل شما ارسال شد. لطفاً صندوق ورودی خود را بررسی کنید.
      </Typography>
    );
  }

  return (
    <StyledForm noValidate onSubmit={handleSubmit}>
      <Typography sx={{ textAlign: 'center' }}>
        ایمیل حساب کاربری خود را وارد کنید تا لینک بازیابی رمز عبور برایتان ارسال شود.
      </Typography>
      <TextField label="ایمیل" name="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      {err && <Typography color="error" sx={{ mb: 1 }}>{err}</Typography>}
      <StyledButton type="submit" disabled={submitting}>
        {submitting ? 'در حال ارسال…' : 'ارسال لینک بازیابی'}
      </StyledButton>
    </StyledForm>
  );
}

function ConfirmForm({ token }) {
  const navigate = useNavigate();
  const [form, setForm] = React.useState({ password: '', password_confirm: '' });
  const [submitting, setSubmitting] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!form.password || !form.password_confirm) {
      setErr('رمز عبور و تکرار آن الزامی است.');
      return;
    }
    if (form.password !== form.password_confirm) {
      setErr('رمز عبور و تکرار آن یکسان نیستند.');
      return;
    }
    let jwtToken;
    try {
      jwtToken = decodeResetToken(token);
    } catch {
      setErr('لینک بازیابی نامعتبر است. لطفاً دوباره درخواست دهید.');
      return;
    }
    try {
      setSubmitting(true);
      await api.post(`/accounts/api/v1/password-reset/confirm/${jwtToken}/`, form);
      navigate('/login', { replace: true });
    } catch (e) {
      const data = e?.response?.data;
      setErr(
        data?.error ||
        data?.password_confirm?.[0] ||
        'تغییر رمز عبور ناموفق بود. ممکن است لینک منقضی شده باشد.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StyledForm noValidate onSubmit={handleSubmit}>
      <Typography sx={{ textAlign: 'center' }}>رمز عبور جدید خود را وارد کنید.</Typography>
      <TextField label="رمز عبور جدید" type="password" name="password" value={form.password} onChange={handleChange} />
      <TextField label="تکرار رمز عبور" type="password" name="password_confirm" value={form.password_confirm} onChange={handleChange} />
      {err && <Typography color="error" sx={{ mb: 1 }}>{err}</Typography>}
      <StyledButton type="submit" disabled={submitting}>
        {submitting ? 'در حال ثبت…' : 'ثبت رمز عبور جدید'}
      </StyledButton>
    </StyledForm>
  );
}

export default function PasswordReset() {
  const { token } = useParams();

  return (
    <RTL>
      <PageBox>
        <InnerBox>
          <StyledLogo src={logo} alt="برنا" />
          {token ? <ConfirmForm token={token} /> : <RequestForm />}
          <Divider sx={{ width: '100%' }} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            <AccentLink to="/login">بازگشت به صفحه ورود</AccentLink>
          </Typography>
        </InnerBox>
      </PageBox>
    </RTL>
  );
}
